import { format } from 'date-fns';
import { de } from 'date-fns/locale';

/**
 * Utility-Funktionen für die Datumsformatierung
 */
class DateUtils {
  /**
   * Formatiert ein Datum im deutschen Format
   * @param {string|Date} date - Das zu formatierende Datum (DD.MM.YYYY oder Date-Objekt)
   * @param {string} formatStr - Das Format-String für date-fns
   * @returns {string} Das formatierte Datum
   */
  static formatDate(date, formatStr = 'dd. MMM yyyy') {
    if (!date) return '';
    
    try {
      let dateObj;
      
      if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === 'string') {
        // Prüfen, ob das Datum im deutschen Format (DD.MM.YYYY) vorliegt
        if (date.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
          const [day, month, year] = date.split('.');
          dateObj = new Date(`${year}-${month}-${day}`);
        } else {
          // Versuchen, das Datum als ISO-String zu parsen
          dateObj = new Date(date);
        }
      } else {
        return '';
      }
      
      // Prüfen, ob das Datum gültig ist
      if (isNaN(dateObj.getTime())) {
        console.error('Ungültiges Datum:', date);
        return date;
      }
      
      return format(dateObj, formatStr, { locale: de });
    } catch (e) {
      console.error('Fehler beim Formatieren des Datums:', e);
      return date;
    }
  }
  
  /**
   * Berechnet die Dauer zwischen zwei Datumsangaben in Tagen
   * @param {string} startDate - Startdatum im Format DD.MM.YYYY
   * @param {string} endDate - Enddatum im Format DD.MM.YYYY
   * @returns {string|null} Die Dauer in Tagen oder null bei Fehler
   */
  static getDuration(startDate, endDate) {
    if (!startDate || !endDate) return null;
    
    try {
      const [startDay, startMonth, startYear] = startDate.split('.');
      const [endDay, endMonth, endYear] = endDate.split('.');
      
      const start = new Date(`${startYear}-${startMonth}-${startDay}`);
      const end = new Date(`${endYear}-${endMonth}-${endDay}`);
      
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays === 1 ? '1 Tag' : `${diffDays} Tage`;
    } catch (e) {
      console.error('Fehler bei der Datumsberechnung:', e);
      return null;
    }
  }
  
  /**
   * Formatiert ein Datum für die Anzeige im Bearbeitungszeitraum
   * @param {string} startDate - Startdatum im Format DD.MM.YYYY
   * @param {string} endDate - Enddatum im Format DD.MM.YYYY oder null
   * @returns {string} Der formatierte Bearbeitungszeitraum
   */
  static formatProcessingPeriod(startDate, endDate) {
    if (!startDate) return '';
    
    try {
      const formattedStart = this.formatDate(startDate, 'dd. MMM');
      const formattedEnd = endDate ? this.formatDate(endDate, 'dd. MMM yyyy') : 'heute';
      
      return `${formattedStart} - ${formattedEnd}`;
    } catch (e) {
      console.error('Fehler beim Formatieren des Bearbeitungszeitraums:', e);
      return `${startDate} - ${endDate || 'heute'}`;
    }
  }
}

export default DateUtils;
